import type { CatalogExercise, Exercise } from "../api/types";
import { rowType } from "./activity";

/** A blank row: strength with the usual 3×10, like before step 1. */
export function newExercise(): Exercise {
  return {
    name: "",
    sets: 3,
    reps: 10,
    weight: null,
    exerciseType: "strength",
    muscleGroups: [],
    notes: null,
    catalogExerciseId: null,
  };
}

/**
 * Row fields to apply when a catalog exercise is picked. Values that belong to
 * another type are cleared, so switching Футбол → Жим never keeps a duration.
 */
export function catalogPatch(item: CatalogExercise, current: Exercise): Partial<Exercise> {
  const patch: Partial<Exercise> = {
    name: item.name,
    muscleGroups: [...(item.muscleGroups ?? [])],
    catalogExerciseId: item.id,
    exerciseType: item.exerciseType,
    icon: item.icon,
  };
  if (rowType(current) !== item.exerciseType) {
    const strength = item.exerciseType === "strength";
    Object.assign(patch, {
      sets: strength ? 3 : 1,
      reps: strength ? 10 : 1,
      weight: null,
      durationSec: null,
      distanceM: null,
      intensity: null,
      rounds: null,
      invalid: undefined,
    } satisfies Partial<Exercise>);
  }
  return patch;
}

/**
 * Link rows typed by hand to a catalog exercise with the same name, so their
 * type is known before saving (the name field resolves on blur, which a quick
 * tap on Save can beat).
 */
export function resolveRowsFromCatalog(rows: Exercise[], catalog: CatalogExercise[]): Exercise[] {
  return rows.map((row) => {
    if (row.catalogExerciseId || !row.name.trim()) return row;
    const name = row.name.trim().toLowerCase();
    const hit = catalog.find((c) => c.name.trim().toLowerCase() === name);
    return hit ? { ...row, ...catalogPatch(hit, row) } : row;
  });
}

/** Copy of a row for a new workout/template (no id, UI flags dropped). */
export function cloneRow(e: Exercise, index: number, opts?: { copyNotes?: boolean }): Exercise {
  return {
    name: e.name,
    sets: e.sets,
    reps: e.reps,
    weight: e.weight ?? null,
    exerciseType: e.exerciseType,
    icon: e.icon ?? null,
    durationSec: e.durationSec ?? null,
    distanceM: e.distanceM ?? null,
    intensity: e.intensity ?? null,
    rounds: e.rounds ?? null,
    sortOrder: e.sortOrder ?? index,
    muscleGroups: [...(e.muscleGroups ?? [])],
    notes: opts?.copyNotes ? (e.notes ?? null) : null,
    catalogExerciseId: e.catalogExerciseId ?? null,
  };
}
